import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";

Vue.use(Vuex);

const baseUrl =
  (window.location.hostname == "localhost"
    ? "http://" + window.location.hostname + ":3000"
    : window.location.origin) + "/api";

export default new Vuex.Store({
  state: {
    categories: [],
    listings: [],
    services: [],
    listingsByCategory: [],
    currentCategory: {},
    currentListing: {},
    tagsByCategory: {},
    searchTerms: [],
    searchResults: [],
    aboutPageData: {},
    images: {},
    sponsors: [],
    adminToken: "",
    kioskMode: false,
    userSearches: []
  },
  getters: {
    headers(state) {
      return { Authorization: `Bearer ${state.adminToken}` };
    },
    baseUrl: () => {
      return (
        (window.location.hostname == "localhost"
          ? "http://" + window.location.hostname + ":3000"
          : window.location.origin) + "/api"
      );
    }
  },
  mutations: {
    assignCategories: (state, response) => {
      state.categories = response.data.data;
    },
    assignListings: (state, response) => {
      state.listings = response.data.data;
    },
    assignServices: (state, response) => {
      state.services = response.data.data;
    },
    assignListingsByCategory: (state, response) => {
      // we convert the on- vs. off-post information to a tag
      response.data.data.forEach(listing =>
        listing.tags.push(listing.onbase ? "_onbase" : "_offbase")
      );
      state.listingsByCategory = response.data.data;
    },
    assignCurrentCategory: (state, response) => {
      state.currentCategory = response.data.data;
    },
    assignCurrentListing: (state, response) => {
      state.currentListing = response.data.data;
    },
    assignTagsByCategory: (state, response) => {
      state.tagsByCategory = response.data.data;
    },
    assignSearchTerms: (state, response) => {
      state.searchTerms = response.data.data;
    },
    assignSearchResults: (state, response) => {
      state.searchResults = response.data.data;
    },
    assignAboutPageData: (state, response) => {
      state.aboutPageData = response.data.data;
    },
    assignImages: (state, response) => {
      Vue.set(state.images, response.data.data.id, response.data.data.imgsrc);
    },
    assignSponsors: (state, response) => {
      state.sponsors = response.data.data;
    },
    assignAdminToken: (state, response) => {
      state.adminToken = response.data.token;
    },
    assignKioskMode: (state, mode) => {
      state.kioskMode = mode;
    },
    assignUserSearches: (state, response) => {
      state.userSearches = response.data.data;
    }    
  },
  actions: {
    /* categories */
    fetchCategories(context) {
      axios
        .get(`${baseUrl}/categories`)
        .then(response => context.commit("assignCategories", response));
    },
    fetchCategoriesForceUpdate(context) {
      // we don't want the browser to use a cached response
      // so we use the trick of adding a timestamp query parameter
      axios
        .get(`${baseUrl}/categories?timestamp=${new Date().getTime()}`)
        .then(response => context.commit("assignCategories", response));
    },
    addCategory(context, payload) {
      axios
        .post(`${baseUrl}/categories`, payload, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchCategoriesForceUpdate"));
    },
    updateCategory(context, payload) {
      axios
        .put(`${baseUrl}/categories/${payload.id}`, payload, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchCategoriesForceUpdate"));
    },
    deleteCategory(context, payload) {
      axios
        .delete(`${baseUrl}/categories/${payload.id}`, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchCategoriesForceUpdate"));
    },

    /* listings */
    fetchListings(context) {
      axios
        .get(`${baseUrl}/listings`)
        .then(response => context.commit("assignListings", response));
    },
    fetchListingsForceUpdate(context) {
      // we don't want the browser to use a cached response
      // so we use the trick of adding a timestamp query parameter
      axios
        .get(`${baseUrl}/listings?timestamp=${new Date().getTime()}`)
        .then(response => context.commit("assignListings", response));
    },
    addListing(context, payload) {
      axios
        .post(`${baseUrl}/listings`, payload, { headers: this.getters.headers })
        .then(() => this.dispatch("fetchListingsForceUpdate"));
    },
    updateListing(context, payload) {
      axios
        .put(`${baseUrl}/listings/${payload.id}`, payload, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchListingsForceUpdate"));
    },
    deleteListing(context, payload) {
      axios
        .delete(`${baseUrl}/listings/${payload.id}`, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchListingsForceUpdate"));
    },

    /* services */
    fetchServices(context) {
      axios
        .get(`${baseUrl}/services`)
        .then(response => context.commit("assignServices", response));
    },
    fetchServicesForceUpdate(context) {
      // we don't want the browser to use a cached response
      // so we use the trick of adding a timestamp query parameter
      axios
        .get(`${baseUrl}/services?timestamp=${new Date().getTime()}`)
        .then(response => context.commit("assignServices", response));
    },
    addService(context, payload) {
      axios
        .post(`${baseUrl}/services`, payload, { headers: this.getters.headers })
        .then(() => this.dispatch("fetchServicesForceUpdate"));
    },
    updateService(context, payload) {
      axios
        .put(`${baseUrl}/services/${payload.id}`, payload, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchServicesForceUpdate"));
    },
    deleteService(context, payload) {
      axios
        .delete(`${baseUrl}/services/${payload.id}`, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchServicesForceUpdate"));
    },
    addTag(context, payload) {
      axios
        .post(`${baseUrl}/services/tags`, payload, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchServicesForceUpdate"));
    },
    updateTag(context, payload) {
      axios
        .put(`${baseUrl}/services/tags/${payload.id}`, payload, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchServicesForceUpdate"));
    },
    deleteTag(context, payload) {
      axios
        .delete(`${baseUrl}/services/tags/${payload.id}`, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchServicesForceUpdate"));
    },

    /* listingsByCategory */
    fetchListingsByCategory(context, payload) {
      axios
        .get(`${baseUrl}/listings/`, { params: { category: payload.id } })
        .then(response => context.commit("assignListingsByCategory", response));
    },

    /* currentCategory */
    fetchCategory(context, payload) {
      axios
        .get(`${baseUrl}/categories/${payload.id}`)
        .then(response => context.commit("assignCurrentCategory", response));
    },

    /* currentListing */
    fetchListing(context, payload) {
      axios
        .get(`${baseUrl}/listings/${payload.id}`)
        .then(response => context.commit("assignCurrentListing", response));
    },

    /* tagsByCategory **/
    fetchTagsByCategory(context, payload) {
      axios
        .get(`${baseUrl}/services`, { params: { category: payload.id } })
        .then(response => context.commit("assignTagsByCategory", response));
    },

    /* searchTerms */
    fetchSearchTerms(context) {
      axios
        .get(`${baseUrl}/search/terms`)
        .then(response => context.commit("assignSearchTerms", response));
    },

    /* searchResults */
    fetchSearchResults(context, payload) {
      axios
        .get(`${baseUrl}/search`, { params: { term: payload.searchTerm } })
        .then(response => context.commit("assignSearchResults", response));
    },

    /* aboutPageData */
    fetchAboutPageData(context) {
      axios
        .get(`${baseUrl}/about/`)
        .then(response => context.commit("assignAboutPageData", response));
    },

    /* images */
    fetchImage(context, payload) {
      axios
        .get(`${baseUrl}/images/${payload.id}`)
        .then(response => context.commit("assignImages", response));
    },

    /* sponsors */
    fetchSponsors(context) {
      axios
        .get(`${baseUrl}/sponsors`)
        .then(response => context.commit("assignSponsors", response));
    },
    fetchSponsorsForceUpdate(context) {
      // we don't want the browser to use a cached response
      // so we use the trick of adding a timestamp query parameter
      axios
        .get(`${baseUrl}/sponsors?timestamp=${new Date().getTime()}`)
        .then(response => context.commit("assignSponsors", response));
    },
    updateSponsors(context, payload) {
      axios
        .put(`${baseUrl}/sponsors`, payload, { headers: this.getters.headers })
        .then(() => this.dispatch("fetchSponsorsForceUpdate"));
    },
    /* user searches */
    fetchUserSearches(context) {
      axios
        .get(`${baseUrl}/user-searches`)
        .then(response => context.commit("assignUserSearches", response));
    },   
    /* adminToken */
    login(context, payload) {
      axios
        .post(`${baseUrl}/login`, payload)
        .then(response => context.commit("assignAdminToken", response));
    },
    changePassword(context, payload) {
      axios.put(`${baseUrl}/users`, payload);
    }
  }
});
